import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import withAuth from "../../utils/withAuth";
import { fetchCallAllDataByIDAPI } from "../../utils/api";
import ContentWrapper from "../../components/contentwrapper/ContentWrapper";
import ReportNavBar from "../../components/ReportNavBar";

const SKELETON_COUNT = 10;

const SkeletonTableCard = () => (
    <div className="col-md-4 col-lg-3 col-sm-6 mb-4">
        <div className="card h-100 shadow-sm">
            <div className="card-body p-2">
                <div className="skeleton" style={{ height: 18, width: "70%", marginBottom: 10 }} />
                <div className="skeleton" style={{ height: 14, width: "100%", marginBottom: 6 }} />
                <div className="skeleton" style={{ height: 14, width: "100%", marginBottom: 6 }} />
                <div className="skeleton" style={{ height: 14, width: "80%", marginBottom: 6 }} />
            </div>
        </div>
    </div>
);

const perCallCost = (spend, calls) => {
    const c = Number(calls || 0);
    const s = Number(spend || 0);
    if (c <= 0) return 0;
    return s / c;
};

const iso = (d) => new Date(d).toISOString().slice(0, 10);

const YesterdayCallCount = () => {
    const [process, setProcess] = useState([]);     // API rows
    const [summary, setSummary] = useState(null);   // API summary
    const [isLoading, setIsLoading] = useState(false);

    // ✅ Date range filters (default = yesterday)
    const [fromDate, setFromDate] = useState(() => iso(Date.now() - 86400000));
    const [toDate, setToDate] = useState(() => iso(Date.now() - 86400000));
    const [appliedDate, setAppliedDate] = useState({ from: "", to: "" });

    // ✅ Skill search (applied)
    const [skillQuery, setSkillQuery] = useState("");
    const [appliedSkillQuery, setAppliedSkillQuery] = useState("");

    // ✅ Center multi-select (checkbox)
    const [centerOpen, setCenterOpen] = useState(false);
    const [centerSearch, setCenterSearch] = useState("");
    const [selectedCenters, setSelectedCenters] = useState(new Set());
    const [appliedCenters, setAppliedCenters] = useState(new Set());

    const navigate = useNavigate();
    const ppcid = localStorage.getItem("ppcdata");

    useEffect(() => {
        if (!ppcid) return navigate("/");
        navigate("/yesterday-call-report");

        // default load = yesterday
        const y = iso(Date.now() - 86400000);
        setAppliedDate({ from: y, to: y });
        getLiveCallWithProcessName(ppcid, y, y);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ppcid]);

    const getLiveCallWithProcessName = async (id, d1, d2) => {
        setIsLoading(true);
        try {
            const qs = new URLSearchParams();
            if (d1) qs.set("start_date", d1);
            if (d2) qs.set("end_date", d2);

            const res = await fetchCallAllDataByIDAPI(
                `ppc-team-get-yesterday-call-with-proccess-name-by-id/${id}?${qs.toString()}`
            );

            if (res?.data?.status === 1) {
                const p = res?.data?.process || {};
                const rows = Array.isArray(p?.rows) ? p.rows : [];
                setProcess(rows);
                setSummary(p?.summary || null);
            } else {
                toast.error(res?.data?.message || "Failed to load process");
                setProcess([]);
                setSummary(null);
            }
        } catch (e) {
            console.error(e);
            toast.error("Network/CORS error while loading process");
            setProcess([]);
            setSummary(null);
        } finally {
            setIsLoading(false);
        }
    };

    // ✅ Updated PERSON_BY_CODE same like TodayLiveCall
    const PERSON_BY_CODE = {
        MP: "REWA",
        DI: "DINESH",
        PU: "PUSHPENDAR",
        RI: "RICHA",
        MA: "MAHADESH",
        MY: "MYSORE CENTER",
        KRL: "KERALA",
        PL: "RAVI",
        TN: "MAHALAXMI",
        MU: "MURGESH",
        RCP: "RAHUL",
        G19: "KANTA",
        G10: "RAVI",
        SCP: "SHRINIWAS",
        CH: "RAVI",
        TRK: "TARAK",
        BA: "BANGLORE",
        SL: "SHAILENDRA",
        NOI: "ABHISHEK",
        CT: "CT",
        AM: "AMIT",
        KA: "KANIKA",
        SRK: "SHAHRUKH",
        JGT: "JAGAT",
        RNOI: "RAVI NOIDA",
        GNOI: "RAVI NOIDA",
        JS: "PUSHPENDAR",
        NI: "PUSHPENDAR",
        AN: "PUSHPENDAR",
        GZB: "GHAZIABAD",
        BL: "BALLARY",
        RV: "RAVINDRA",
        KO: "KOHAT NORTH",
        MN: "RAHUL",
        BU: "BURARI",
        D2C: "D2C",
        WA: "WHATSAPP",
        WH: "WHATSAPP",
        UN: "MAYANK",
        VP: "VIKAS PURI",
        SP: "SANJAY ASTRO",
        TG: "TG",
        NSP: "NSP",
        KOL: "KOLKATA",
        "RI_MA": "MAHADESH",
        "RCP_MN": "RAHUL MOTI NAGAR",
    };

    const deriveGroupName = (skillRaw) => {
        const skill = String(skillRaw || "").trim();
        if (!skill) return "Other";
        const parts = skill.split("_").filter(Boolean);
        const n = parts.length;
        const last = parts[n - 1]?.toUpperCase() || "";
        const last2 = n >= 2 ? `${parts[n - 2].toUpperCase()}_${last}` : null;

        if (last2 && PERSON_BY_CODE[last2]) return PERSON_BY_CODE[last2];
        if (PERSON_BY_CODE[last]) return PERSON_BY_CODE[last];
        return last2 ? (PERSON_BY_CODE[last2] || last2) : (last || "Other");
    };

    const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
    const fmtMoney = (n) =>
        Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    // ✅ Group raw data (adds perCall + perf)
    const groupedDataRaw = useMemo(() => {
        const groups = {};
        (process || []).forEach((row) => {
            const code = String(row?.skillname || "");
            const calls = Number(row?.unique_calls || 0);
            const orders = Number(row?.order_count || 0);
            const spend = Number(row?.spend_yesterday || 0);
            const groupName = deriveGroupName(code);

            if (!groups[groupName]) {
                groups[groupName] = { name: groupName, totalCalls: 0, totalOrders: 0, totalSpend: 0, items: [] };
            }

            groups[groupName].totalCalls += calls;
            groups[groupName].totalOrders += orders;
            groups[groupName].totalSpend += spend;

            groups[groupName].items.push({
                code,
                calls,
                orders,
                spend,
                perCall: perCallCost(spend, calls),
                perf: calls ? (orders / calls) * 100 : 0,
            });
        });

        Object.values(groups).forEach((g) => {
            g.perf = g.totalCalls ? (g.totalOrders / g.totalCalls) * 100 : 0;
            g.perCall = perCallCost(g.totalSpend, g.totalCalls);
            g.items.sort((a, b) => (b.calls !== a.calls ? b.calls - a.calls : a.code.localeCompare(b.code)));
        });

        return Object.values(groups).sort((a, b) =>
            b.totalCalls !== a.totalCalls ? b.totalCalls - a.totalCalls : a.name.localeCompare(b.name)
        );
    }, [process]);

    // ✅ center options (for checkbox list)
    const centerOptions = useMemo(() => {
        return Array.from(new Set(groupedDataRaw.map((g) => g.name).filter(Boolean))).sort((a, b) => a.localeCompare(b));
    }, [groupedDataRaw]);

    const filteredCenterOptions = useMemo(() => {
        const q = String(centerSearch || "").trim().toLowerCase();
        if (!q) return centerOptions;
        return centerOptions.filter((c) => c.toLowerCase().includes(q));
    }, [centerOptions, centerSearch]);

    // ✅ initialize center selection to ALL if empty
    useEffect(() => {
        if (!centerOptions.length) return;
        setSelectedCenters((prev) => (prev && prev.size ? prev : new Set(centerOptions)));
        setAppliedCenters((prev) => (prev && prev.size ? prev : new Set(centerOptions)));
    }, [centerOptions]);

    const toggleCenter = (name) => {
        setSelectedCenters((prev) => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name);
            else next.add(name);
            return next;
        });
    };

    const onSelectAllCenters = () => setSelectedCenters(new Set(centerOptions));
    const onClearCenters = () => setSelectedCenters(new Set());

    const onApplyCentersOnly = () => {
        if (!selectedCenters.size) {
            const all = new Set(centerOptions);
            setSelectedCenters(all);
            setAppliedCenters(all);
            toast.info("No center selected, showing All Centers");
            setCenterOpen(false);
            return;
        }
        setAppliedCenters(new Set(selectedCenters));
        setCenterOpen(false);
    };

    const onResetCenters = () => {
        const all = new Set(centerOptions);
        setSelectedCenters(all);
        setAppliedCenters(all);
        setCenterSearch("");
        setCenterOpen(false);
    };

    const centerButtonText = useMemo(() => {
        if (!appliedCenters || appliedCenters.size === 0) return "All Centers";
        if (appliedCenters.size === centerOptions.length) return "All Centers";
        if (appliedCenters.size === 1) return Array.from(appliedCenters)[0];
        return `${appliedCenters.size} Centers`;
    }, [appliedCenters, centerOptions]);

    // ✅ apply Center + Skill filter on grouped data
    const groupedData = useMemo(() => {
        const q = String(appliedSkillQuery || "").trim().toLowerCase();
        const centers = appliedCenters && appliedCenters.size ? appliedCenters : new Set(centerOptions);

        let list = groupedDataRaw.filter((g) => centers.has(g.name));

        if (q) {
            list = list
                .map((g) => {
                    const items = (g.items || []).filter((it) => String(it.code || "").toLowerCase().includes(q));
                    if (!items.length) return null;

                    const totalCalls = items.reduce((s, it) => s + (Number(it.calls) || 0), 0);
                    const totalOrders = items.reduce((s, it) => s + (Number(it.orders) || 0), 0);
                    const totalSpend = items.reduce((s, it) => s + (Number(it.spend) || 0), 0);

                    return {
                        ...g,
                        items,
                        totalCalls,
                        totalOrders,
                        totalSpend,
                        perf: totalCalls ? (totalOrders / totalCalls) * 100 : 0,
                        perCall: perCallCost(totalSpend, totalCalls),
                    };
                })
                .filter(Boolean);
        }

        return list;
    }, [groupedDataRaw, appliedCenters, centerOptions, appliedSkillQuery]);

    // ✅ Prefer backend summary if present
    const yCallCount = useMemo(() => {
        if (summary && typeof summary.total_unique_calls !== "undefined") return Number(summary.total_unique_calls) || 0;
        return (process || []).reduce((s, r) => s + (Number(r?.unique_calls) || 0), 0);
    }, [process, summary]);

    const yOrderCount = useMemo(() => {
        if (summary && typeof summary.total_orders_created !== "undefined") return Number(summary.total_orders_created) || 0;
        return (process || []).reduce((s, r) => s + (Number(r?.order_count) || 0), 0);
    }, [process, summary]);

    const ySpend = useMemo(() => {
        if (summary && typeof summary.total_spend_yesterday !== "undefined") return Number(summary.total_spend_yesterday) || 0;
        return (process || []).reduce((s, r) => s + (Number(r?.spend_yesterday) || 0), 0);
    }, [process, summary]);

    const yPerf = useMemo(() => (yCallCount ? (yOrderCount / yCallCount) * 100 : 0), [yCallCount, yOrderCount]);
    const yPerCall = useMemo(() => perCallCost(ySpend, yCallCount), [ySpend, yCallCount]);

    const headerTitle = useMemo(() => {
        const d1 = appliedDate.from || fromDate;
        const d2 = appliedDate.to || toDate;
        if (d1 && d2) return `Yesterday Report (${d1} → ${d2})`;
        return "Yesterday Report";
    }, [appliedDate, fromDate, toDate]);

    // ✅ Apply button for date + skill + centers (triggers API)
    const onApplyFilters = () => {
        const d1 = String(fromDate || "");
        const d2 = String(toDate || "");
        if (!d1 || !d2) return toast.error("Please select From Date and To Date");
        if (d1 > d2) return toast.error("From Date cannot be greater than To Date");

        setAppliedDate({ from: d1, to: d2 });
        setAppliedSkillQuery(skillQuery || "");
        setAppliedCenters(new Set(selectedCenters.size ? selectedCenters : centerOptions));
        setCenterOpen(false);

        if (ppcid) getLiveCallWithProcessName(ppcid, d1, d2);
    };

    const onResetFilters = () => {
        const y = iso(Date.now() - 86400000);
        setFromDate(y);
        setToDate(y);
        setSkillQuery("");
        setAppliedSkillQuery("");

        const all = new Set(centerOptions);
        setSelectedCenters(all);
        setAppliedCenters(all);

        setAppliedDate({ from: y, to: y });
        setCenterSearch("");
        setCenterOpen(false);

        if (ppcid) getLiveCallWithProcessName(ppcid, y, y);
    };

    return (
        <div className="misDashboard">
            <Helmet>
                <title>SATKARTAR::DASHBOARD</title>
            </Helmet>

            <style>{`
        .skeleton{position:relative;overflow:hidden;background:#eee;border-radius:6px}
        .skeleton:after{content:"";position:absolute;inset:0;transform:translateX(-100%);
          background:linear-gradient(90deg,transparent,rgba(255,255,255,.6),transparent);
          animation:shimmer 1.2s infinite}
        @keyframes shimmer{100%{transform:translateX(100%)}}

        /* ✅ full skill name in ONE LINE, no dots */
        .mini-table{ width:100%; table-layout:auto !important; min-width:760px; }
        .mini-table th,.mini-table td{font-size:12px; padding:6px 8px; vertical-align:middle;}
        .skillCell{ white-space:nowrap !important; overflow:visible !important; text-overflow:clip !important; }

        /* ✅ allow horizontal scroll inside card when skill is long */
        .cardScroll{ max-height:260px; overflow-y:auto; overflow-x:auto; }

        /* sticky header inside card scroll */
        .stickyHead { position: sticky; top: 0; z-index: 2; background: #fff; }
        .skillStickyHeader { background:#fff; }
        .colHead th{ background:#fafafa; }

        .filterRow{display:flex;gap:8px;align-items:end;flex-wrap:wrap;}
        .filterBox{display:flex;flex-direction:column;gap:4px;}
        .filterBox label{font-weight:600;font-size:12px;margin:0;}
        .filterBox select{min-width:120px;}

        /* ✅ Center checkbox dropdown */
        .centerDropWrap{ position:relative; min-width:260px; }
        .centerDropBtn{
          width:260px; display:flex; align-items:center; justify-content:space-between;
          padding:6px 10px; border:1px solid rgba(0,0,0,.15);
          border-radius:6px; background:#fff; font-size:13px;
        }
        .centerDropMenu{
          position:absolute; top:100%; left:0; z-index:50; width:260px;
          background:#fff; border:1px solid rgba(0,0,0,.15);
          border-radius:10px; margin-top:6px;
          box-shadow:0 10px 30px rgba(0,0,0,.12);
          overflow:hidden;
        }
        .centerDropHead{padding:10px; border-bottom:1px solid rgba(0,0,0,.08);}
        .centerSearch{
          width:100%; border:1px solid rgba(0,0,0,.15);
          border-radius:8px; padding:7px 10px; font-size:13px; outline:none;
        }
        .centerActions{
          display:flex; padding:10px; border-bottom:1px solid rgba(0,0,0,.08);
          justify-content:space-between;
        }
        .centerList{max-height:240px; overflow:auto; padding:8px 5px;}
        .centerItem{display:flex; align-items:center; gap:10px; padding:6px 2px; font-size:13px;}
        .centerFoot{
          display:flex; gap:8px; justify-content:space-between;
          padding:10px; border-top:1px solid rgba(0,0,0,.08); background:#fafafa;
        }
      `}</style>

            <ContentWrapper>
                <div className="row">
                    <div className="col-12">
                        <ReportNavBar />
                    </div>
                </div>

                {/* ✅ Top Summary */}
                <div className="">
                    <h5 className="livecallcount">
                        {headerTitle}
                        {"  |  "}
                        Calls: {isLoading ? "..." : fmt(yCallCount)}
                        {"  |  "}
                        Orders: {isLoading ? "..." : fmt(yOrderCount)}
                        {"  |  "}
                        Spend: {isLoading ? "..." : `₹${fmtMoney(ySpend)}`}
                        {"  |  "}
                        Per Call:{" "}
                        <span style={{ color: yPerCall > 100 ? "#d32f2f" : "#111", fontWeight: 700 }}>
                            {isLoading ? "..." : `₹${fmtMoney(yPerCall)}`}
                        </span>
                        {"  |  "}
                        Perf: {isLoading ? "..." : `${yPerf.toFixed(1)}%`}
                    </h5>
                </div>

                {/* ✅ Filters */}
                <div className="card shadow-sm mt-2">
                    <div className="card-body p-2">
                        <div className="filterRow">
                            <div className="filterBox">
                                <label>From Date</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>

                            <div className="filterBox">
                                <label>To Date</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>

                            {/* ✅ Center checkbox dropdown */}
                            <div className="filterBox">
                                <label>Center Name</label>
                                <div className="centerDropWrap">
                                    <button
                                        type="button"
                                        className="centerDropBtn"
                                        onClick={() => setCenterOpen((s) => !s)}
                                        disabled={isLoading || !centerOptions.length}
                                    >
                                        <span style={{ fontWeight: 600 }}>{centerButtonText}</span>
                                        <span style={{ opacity: 0.7 }}>▾</span>
                                    </button>

                                    {centerOpen && (
                                        <div className="centerDropMenu">
                                            <div className="centerDropHead">
                                                <input
                                                    className="centerSearch"
                                                    value={centerSearch}
                                                    onChange={(e) => setCenterSearch(e.target.value)}
                                                    placeholder="Search center..."
                                                />
                                            </div>

                                            <div className="centerActions">
                                                <button type="button" className="btnselectall" onClick={onSelectAllCenters}>
                                                    Select All
                                                </button>
                                                <button type="button" className="btnresetall" onClick={onClearCenters}>
                                                    Clear
                                                </button>
                                            </div>

                                            <div className="centerList">
                                                {filteredCenterOptions.length ? (
                                                    filteredCenterOptions.map((name) => (
                                                        <label key={name} className="centerItem">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedCenters.has(name)}
                                                                onChange={() => toggleCenter(name)}
                                                            />
                                                            <span>{name}</span>
                                                        </label>
                                                    ))
                                                ) : (
                                                    <div className="text-muted" style={{ fontSize: 13, padding: "8px 2px" }}>
                                                        No centers found
                                                    </div>
                                                )}
                                            </div>

                                            <div className="centerFoot">
                                                <button type="button" className="btnselectall" onClick={onResetCenters}>
                                                    Reset All
                                                </button>
                                                <button type="button" className="btnresetall" onClick={onApplyCentersOnly}>
                                                    Apply Centers
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* ✅ Skill search */}
                            <div className="filterBox">
                                <label>Search Skill</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Search skill..."
                                    value={skillQuery}
                                    onChange={(e) => setSkillQuery(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>

                            <button className="btnskillapply" onClick={onApplyFilters} disabled={isLoading}>
                                Apply
                            </button>

                            <button className="btnresetall" onClick={onResetFilters} disabled={isLoading}>
                                Reset
                            </button>
                        </div>
                    </div>
                </div>

                {/* ✅ Cards */}
                <div className="row mt-3 livecallbody">
                    {isLoading
                        ? Array.from({ length: SKELETON_COUNT }).map((_, i) => <SkeletonTableCard key={`sk-${i}`} />)
                        : groupedData.map((group, idx) => (
                            <div className="col-md-6 col-lg-4 mb-3" key={idx}>
                                <div className="card shadow-sm">
                                    <div className="card-body p-2">
                                        <div className="p-0 cardScroll">
                                            <table className="table table-sm table-bordered mb-0 mini-table">
                                                <thead className="stickyHead">
                                                    <tr>
                                                        <th colSpan={6} className="skillStickyHeader">
                                                            <div className="d-flex justify-content-between">
                                                                <div className="sh-left">{group.name}</div>
                                                                <div className="sh-right">
                                                                    <span>Calls: <b>{fmt(group.totalCalls)}</b></span>{" "}
                                                                    <span>| Orders: <b>{fmt(group.totalOrders)}</b></span>{" "}
                                                                    <span>| ₹{fmtMoney(group.totalSpend)}</span>{" "}
                                                                    <span>
                                                                        | Per Call:{" "}
                                                                        <b style={{ color: group.perCall > 100 ? "#d32f2f" : "#111" }}>
                                                                            ₹{fmtMoney(group.perCall)}
                                                                        </b>
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </th>
                                                    </tr>

                                                    <tr className="colHead">
                                                        <th>Skill</th>
                                                        <th style={{ width: 40, textAlign: "right" }}>Calls</th>
                                                        <th style={{ width: 40, textAlign: "right" }}>Orders</th>
                                                        <th style={{ width: 70, textAlign: "right" }}>Spend</th>
                                                        <th style={{ width: 70, textAlign: "right" }}>Cost</th>
                                                        <th style={{ width: 55, textAlign: "right" }}>Perf</th>
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {group.items.map((item, ix) => {
                                                        const isGood = item.calls > 0 && item.perf >= 15;
                                                        const perfColor = item.calls === 0 ? "#999" : isGood ? "#2e7d32" : "#d32f2f";
                                                        const arrow = item.calls === 0 ? "—" : isGood ? "↑" : "↓";
                                                        const perCallColor = item.perCall > 100 ? "#d32f2f" : "#111";

                                                        return (
                                                            <tr key={ix}>
                                                                <td className="skillCell">{item.code}</td>
                                                                <td style={{ textAlign: "right" }}>{fmt(item.calls)}</td>
                                                                <td style={{ textAlign: "right" }}>{fmt(item.orders)}</td>
                                                                <td style={{ textAlign: "right" }}>₹{fmtMoney(item.spend)}</td>
                                                                <td style={{ textAlign: "right", color: perCallColor }}>
                                                                    ₹{fmtMoney(item.perCall)}
                                                                </td>
                                                                <td style={{ textAlign: "right", fontWeight: 800, color: perfColor, display: "flex" }}>
                                                                    <span style={{ display: "inline-block", minWidth: 14, textAlign: "center" }}>
                                                                        {arrow}
                                                                    </span>{" "}
                                                                    <span>{item.calls === 0 ? "0.0%" : `${item.perf.toFixed(1)}%`}</span>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                    {!isLoading && groupedData.length === 0 && (
                        <div className="col-12">
                            <div className="alert alert-info mb-0">No data for selected filters.</div>
                        </div>
                    )}
                </div>
            </ContentWrapper>
        </div>
    );
};

export default withAuth(YesterdayCallCount);
